

/**
 * Check if a product requires a prescription
 * @param {Object} product - Product object with Prescription field
 * @returns {boolean} - True if prescription is required
 */
export const isPrescriptionRequired = (product) => {
  if (!product || product.Prescription === undefined || product.Prescription === null) {
    return false;
  }
  
  // Handle string values from database
  if (typeof product.Prescription === 'string') {
    return product.Prescription.toLowerCase() === 'true';
  }
  
  // Handle boolean values
  if (typeof product.Prescription === 'boolean') {
    return product.Prescription;
  }
  
  // Handle numeric values (1 = true, 0 = false)
  if (typeof product.Prescription === 'number') {
    return product.Prescription === 1;
  }
  
  return false;
};

/**
 * Get prescription status text for display
 * @param {Object} product - Product object with Prescription field
 * @returns {string} - Display text for prescription status
 */
export const getPrescriptionStatusText = (product) => {
  return isPrescriptionRequired(product) ? 'Prescription Required' : 'Available';
};

/**
 * Get prescription button text
 * @param {Object} product - Product object with Prescription field
 * @returns {string} - Button text based on prescription requirement
 */
export const getPrescriptionButtonText = (product) => {
  return isPrescriptionRequired(product) ? 'Upload Prescription' : 'Add to Cart';
};

/**
 * Get CSS classes for prescription status badge
 * @param {Object} product - Product object with Prescription field
 * @returns {string} - CSS classes for styling
 */
export const getPrescriptionBadgeClasses = (product) => {
  const baseClasses = 'px-3 py-1 rounded-full text-sm font-medium';
  const prescriptionClasses = isPrescriptionRequired(product) 
    ? 'bg-red-500 text-white' 
    : 'bg-secondary-500 text-white';
  
  return `${baseClasses} ${prescriptionClasses}`;
};

/**
 * Get CSS classes for prescription button
 * @param {Object} product - Product object with Prescription field
 * @returns {string} - CSS classes for styling
 */
export const getPrescriptionButtonClasses = (product) => {
  const baseClasses = 'flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-300 shadow-medium hover:shadow-large';
  const prescriptionClasses = isPrescriptionRequired(product)
    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
    : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800';
  
  return `${baseClasses} ${prescriptionClasses}`;
};

/**
 * Filter products by prescription requirement
 * @param {Array} products - Array of products
 * @param {boolean} requiresPrescription - Filter for prescription products (true) or non-prescription (false)
 * @returns {Array} - Filtered products
 */
export const filterProductsByPrescription = (products, requiresPrescription) => {
  if (!Array.isArray(products)) return [];
  
  return products.filter(product => 
    isPrescriptionRequired(product) === requiresPrescription
  );
};

/**
 * Check if cart has any prescription items
 * @param {Array} cartItems - Array of cart items
 * @returns {boolean} - True if cart contains prescription items
 */
export const cartHasPrescriptionItems = (cartItems) => {
  if (!Array.isArray(cartItems)) return false;
  
  return cartItems.some(item => isPrescriptionRequired(item));
};
